import { LitElement, createRef, css, html, nothing, ref, when } from '@lit'
import { storefrontClient } from '@nova/storefront'
import styles from '@nova/styles'
import { formatPrice, t } from '@nova/utils'

class NovaMiniCart extends LitElement {
  static properties = {
    dustMessage: {
      type: String,
      attribute: 'dust-message',
    },
    emptyMessage: {
      type: String,
      attribute: 'empty-message',
    },
    ctaText: {
      type: String,
      attribute: 'cta-text',
    },
    ctaUrl: {
      type: String,
      attribute: 'cta-url',
    },
    initialItems: {
      type: Array,
    },
    items: {
      type: Array,
      state: true,
    },
    compareAtPrices: {
      type: Object,
      state: true,
    },
    continueSell: {
      type: Object,
      state: true,
    },
    maxValues: {
      type: Object,
      state: true,
    },
    giftMessage: {
      type: String,
      attribute: 'gift-message',
    },
    giftMessageShow: {
      type: Boolean,
      attribute: 'gift-message-show',
      converter: (value) => value === "true" ? true : false,
    },
    giftMessageTitle: {
      type: String,
      attribute: 'gift-message-title',
    },
    giftMessageDescription: {
      type: String,
      attribute: 'gift-message-description',
    },
  }

  get count() {
    const count = this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
    return count
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.line_price),
      0,
    )
  }

  get formattedSubtotal() {
    return formatPrice(this.subtotal)
  }

  $cartDrawer = createRef()

  constructor() {
    super()
    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
    this.initialItems = []
    this.items = []
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  _handleOpenCart = () => {
    this.$cartDrawer.value.show()
  }

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
    window.location.href = this.ctaUrl || '/collections/all'
  }

  refresh = async (event) => {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.items = data.items
      this.queryVariantData(this.items)

      const open = event?.detail?.open === true;
      if (open) {
        document.dispatchEvent(
          new CustomEvent('nova:cart:open', { bubbles: true })
        );
      }
    } catch {
      console.error('Error fetching cart')
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this.initialItems
      this.queryVariantData(this.items)
    }
    if (changedProperties.has('items')) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:items-count', {
          detail: { count: this.count },
        }),
      )
    }
  }

  async queryVariantData(items) {
    const compareAtPrices = {}
    const continueSell = {}
    const maxValues = {}

    await Promise.all(
      items.map(async (item) => {
        const data = await this.getVariantData(item)
        if (!data) return
        compareAtPrices[item.key] = data.compareAtPrice
        continueSell[item.key] = data.inventoryPolicy
        maxValues[item.key] = data.max
      }),
    )

    this.compareAtPrices = compareAtPrices
    this.continueSell = continueSell
    this.maxValues = maxValues
  }

  getVariantData = async (currentItem) => {
    let results

    const variantData = await storefrontClient.getVariantById(
      currentItem.variant_id,
    )

    if (!variantData) return

    results = {
      compareAtPrice: variantData.compareAtPrice,
      max: variantData.quantityAvailable,
      inventoryPolicy: variantData.currentlyNotInStock,
    }

    return results
  }

  _renderEmptyState = () => {
    return html`
      <div class="empty-state">
        <p>${this.emptyMessage || t('cart.general.empty')}</p>
        <sl-button variant="default" @click=${this._handleCloseCart}>
          ${this.ctaText || t('cart.general.continue_shopping')}
        </sl-button>
        <div></div>
      </div>
    `
  }

  get itemsCount() {
    return this.items.reduce(
      (acc, current) => acc + Number.parseInt(current.quantity),
      0,
    )
  }

  _renderItems = () => {
    return html`
      ${
        __NOVA_GLOBAL__.showFreeShippingProgressBar === true
          ? html`<nova-free-shipping-threshold></nova-free-shipping-threshold>`
          : nothing
      }
      <div class="items">
        ${this.items.map(
          (item) =>
            html`
              <nova-cart-item
                @nova-change=${this.refresh}
                .item=${item}
                .compareAtPrice=${this.compareAtPrices[item.key] || nothing}
                .inventoryPolicy=${this.continueSell[item.key] || nothing}
                .max=${this.maxValues[item.key] || nothing}>
              </nova-cart-item>
            `,
        )}

        ${this.dustMessage ? html `
            <nova-bag-message
              .message=${this.dustMessage}
            ></nova-bag-message>
          ` : nothing
        }

        ${this.giftMessageShow ? html`
          <nova-gift-message
            type="accordion"
            .note=${this.giftMessage}
            .title=${this.giftMessageTitle}
            .description=${this.giftMessageDescription}
          ></nova-gift-message>
        ` : nothing}

        <nosto-campaign placement="minicart-nosto-2" cart-synced></nosto-campaign>
      </div>
      <div slot="footer" class="summary">
        <span class="summary__total">
          <p>${t('cart.general.subtotal')}</p>
          <p>${this.formattedSubtotal}</p>
        </span>
        <sl-button href="/checkout" class="summary__checkout flex">${t('cart.general.checkout')}</sl-button>
        <sl-button href="/cart" class="summary__view-bag">${t('cart.general.view_bag')}</sl-button>
      </div>
    `
  }

  render() {
    return html`
      <sl-drawer label="${t('cart.general.title')} (${this.itemsCount})" class="drawer" ${ref(
        this.$cartDrawer,
      )}>
        ${when(!this.items.length, this._renderEmptyState, this._renderItems)}
      </sl-drawer>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
      }
      .drawer {
        --size: 100%;
        --header-spacing: 30px 30px 20px 30px;
        --body-spacing: 0px 8px;
        --body-spacing: 0px var(--nova-space-2xs);
        --footer-spacing: 0;
      }
      .drawer::part(overlay) {
        fill: var(--nova-utility-overlay, rgba(0, 0, 0, 0.5));
      }
      .drawer::part(header) {
        align-items: center;
        margin: 0 var(--nova-space-2xs);
        border-bottom: 1px solid var(--nova-brand-grey-100);
      }
      .drawer::part(title) {
        color: var(--nova-brand-ink);
        font: var(--nova-h5-font);
        line-height: 135%;
        padding: var(--nova-space-sm) var(--nova-space-2xs);
      }
      .drawer::part(header-actions) {
        padding: var(--nova-space-sm) var(--nova-space-2xs);
      }
      .drawer::part(close-button__base) {
        color: black;
        padding: 0;
        font-size: 20px;
      }
      .empty-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
      }
      .empty-state p {
        font: var(--nova-desktop-body-font);
        margin-block-start: 0;
        margin-block-end: var(--nova-space-xs);
      }
      .empty-state sl-button::part(base) {
        padding: var(--nova-space-xs) var(--nova-space-md-2);
      }
      .summary__checkout {
        display: flex;
      }
      .summary__checkout::part(base) {
        display: flex;
        align-items: center;
        background-color: var(--nova-color-black);
        color: var(--nova-color-white);
        height: var(--nova-space-lg-1);
        min-height: var(--nova-space-lg-1);

        @media screen and (min-width: 768px) {
          min-height: var(--nova-space-md);
          height: var(--nova-space-md);
        }
      }
      sl-button::part(base) {
        background-color: transparent;
        color: var(--nova-brand-ink);
        width: 100%;
        height: var(--nova-button-height-s);
        border: 1px solid var(--nova-brand-grey-100);
        border-radius: 0;
        font: var(--nova-desktop-body-font);
        padding: var(--nova-space-xs) 0;
        align-items: center;
        justify-content: center;
      }
      sl-button::part(label) {
        padding: 0;
      }
      .summary__checkout::part(label) {
        font: var(--nova-body-font);
      }
      .summary__view-bag {
        display: flex;
        margin: var(--nova-space-sm) 0;

        @media screen and (min-width: 768px) {
          margin: var(--nova-space-xs-1) 0;
        }
      }
      .summary__view-bag::part(base) {
        color: var(--nova-color-black);
        height: fit-content;
        min-height: fit-content;
        border: none;
        padding: 0;
      }
      .summary__view-bag::part(label) {
        font: var(--nova-body-font);
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-skip-ink: none;
        text-decoration-thickness: auto;
        text-underline-offset: auto;
        text-underline-position: from-font;
      }
      @media screen and (min-width: 768px) {
        .drawer {
          --body-spacing: 0px 16px;
          --size: 490px;
          --body-spacing: 0px var(--nova-space-xs-1);
        }
        .drawer::part(header) {
          margin: 0 var(--nova-space-xs-1);
        }
        .drawer::part(title) {
          font: var(--nova-h2-font);
          padding: var(--nova-space-xs-1);
        }
        .drawer::part(header-actions) {
          padding: var(--nova-space-xs-1);
        }
        .empty-state p {
          margin-bottom: 20px;
        }
      }
      .summary {
        border-top: 1px solid var(--nova-brand-grey-100);
        background: var(--nova-color-white);
        padding: 0 var(--nova-space-2xs);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 135%;

        @media screen and (min-width: 768px) {
          padding: 0 var(--nova-space-xs-1);
        }
      }
      .summary__total {
        display: flex;
        justify-content: space-between;
        margin: var(--nova-space-sm) 0;
        padding: 0 var(--nova-space-2xs);

        @media screen and (min-width: 768px) {
          margin: var(--nova-space-xs-1) 0;
          padding: 0 var(--nova-space-sm-1-md);
        }
      }
      .summary__total p {
        margin: 0;
      }
      sl-drawer::part(body) {
        overflow-y: auto;
        scrollbar-gutter: stable;
      }
      nova-bag-message + nova-gift-message {
        border-top: 0;
      }
      @supports (scrollbar-gutter: stable) {
        sl-drawer::part(body) {
          overflow-y: auto;
          scrollbar-gutter: stable both-edges;
        }
      }
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)
